import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CustomDelete, FullTable, SelectActions } from 'components';
import { FindSuccessItem, Images, PermissionList, RolePermission, useModal, useSecondaryModal } from 'utils';
import { appointmentActions, httpRequestsOnSuccessActions } from 'store';
import { ScheduleDetails, MassCompleteInfo } from './modals';
import {apptBody, apptEnums, apptHead, apptNotYet} from './constants';
import { scheduleStatuses } from '../../../pages/schedule/constants';
import { ScheduleInformation } from './scheduleInformation';
import { SignatureActionsMenu } from './signatureActionsMenu';

const ACTION_TYPE = 'GET_APPOINTMENT';
const LOCK_UNLOCK = 'CHANGE_APPOINTMENT_LOCK_UNLOCK';
const CONNECT_RECONNECT = 'CONNECT_RECONNECT_TEMPLATE';
const DELETE_MASS = 'DELETE_MASS_APPTS';
const COMPLETE_MASS_APPTS = 'COMPLETE_MASS_APPTS';

export const ScheduleList = ({ renderParams }) => {
    const { appointments } = useSelector((state) => ({
        appointments: state.appointment.appointments,
    }));
    // const globalServices = useSelector((state) => state.system.services);
    const globalServices = useSelector((state) => state.fundingSource.fundingUniqueServices);
    const dispatch = useDispatch();
    const { open } = useModal();
    const { openSecondary } = useSecondaryModal();
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const [selectedList, setSelectedList] = useState([]);
    const [selectAllList, setSelectAllList] = useState(false);
    const lockUnlockSuccess = FindSuccessItem(LOCK_UNLOCK);
    const successConnect = FindSuccessItem(CONNECT_RECONNECT);
    const filteredAppts = appointments?.appts?.filter((appt) => selectedList?.includes(appt.id));
    const renderedAppts = filteredAppts?.filter((i) => i?.status === scheduleStatuses.COMPLETED);
    const pendingAppts = filteredAppts?.filter((i) => i?.status === scheduleStatuses.PENDING);
    const cancelledAppts = filteredAppts?.filter((i) => i?.status === scheduleStatuses.CANCELLED);
    const deletableAppts = filteredAppts?.filter((appt) => appt?.status !== scheduleStatuses.COMPLETED).map((i) => i?.id);
    const deletableCount = filteredAppts?.length - renderedAppts?.length;
    const [selectedFullList, setSelectedFullList] = useState([]);

    useEffect(() => {
        if (lockUnlockSuccess) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(LOCK_UNLOCK));
            resetSelected();
        }
        // if (info) {
        //     resetSelected()
        // }
    }, [lockUnlockSuccess, info]);

    const handleCheck = (ev, item, name) => {
        ev.stopPropagation();
        ev.preventDefault();
        setSelectAllList(false);

        if (name === 'all') {
            let allList = appointments?.appts?.length === selectedList?.length ? [] : appointments?.appts?.map((i) => i?.id);
            setSelectedList(allList);
            setSelectedFullList(appointments?.appts?.length === selectedList?.length ? [] : appointments?.appts);
        } else {
            const fullList = [...selectedFullList];
            const list = [...selectedList];
            const uniqSet = new Set(list);

            if (!uniqSet.has(item?.id)) {
                list.push(item?.id);
                fullList.push(item);
            } else {
                list.indexOf(item?.id) !== -1 && list.splice(list.indexOf(item?.id), 1);
                const fullIdx = fullList.findIndex((i) => i?.id === item?.id);
                fullIdx !== -1 && fullList.splice(fullIdx, 1);
            }

            setSelectedFullList(fullList);
            setSelectedList(list);
        }
    };

    const lockUnlock = (name) => {
        const lockUnlockType = name === 'lock';
        const subText =
            name === 'lock'
                ? 'Are you sure you want to lock the selected appointments? Once locked, they cannot be edited until unlocked.'
                : 'Are you sure you want to unlock the selected appointments? Once unlocked, they can be edited again.';

        const list = {
            apptIds: selectAllList ? [] : selectedList,
            all: selectAllList,
        };

        open(
            <CustomDelete
                info={`Confirm ${name === 'lock' ? 'Lock' : 'Unlock'} All Appointments`}
                // info={'Confirm Cancel All Appointments'}
                text={subText}
                innerText={name === 'lock' ? 'Lock Selected' : 'Unlock Selected'}
                handleDel={() => dispatch(appointmentActions.changeApptLockUnlock(list, lockUnlockType, renderParams))}
                actionType={LOCK_UNLOCK}
                color={name === 'lock' ? null : 'blue'}
            />
        );
    };

    const resetSelected = () => {
        setSelectedList([]);
        setSelectedFullList([]);
        setSelectAllList(false);
    };

    const deleteMassAppt = () => {
        open(
            <CustomDelete
                info={'Delete Selected Appointments'}
                text={`You’ve selected ${selectedList?.length} appointment(s). ${deletableCount} can be deleted, but ${
                    renderedAppts?.length
                } ${renderedAppts?.length === 1 ? 'is' : 'are'} already rendered and cannot be deleted. They will be skipped.`}
                innerText={`Delete ${deletableCount} Appt${deletableCount !== 1 ? 's' : ''}`}
                handleDel={() =>
                    dispatch(
                        appointmentActions.deleteMassAppt(
                            {
                                apptIds: selectAllList ? [] : deletableAppts,
                                all: selectAllList,
                            },
                            renderParams
                        )
                    )
                }
                actionType={DELETE_MASS}
                afterSuccess={() => resetSelected()}
                disableAction={deletableCount === 0}
                body={
                    <div className="deleting-client-info-box">
                        <img src={Images.infoCircle} alt="icon" />
                        <p style={{ fontWeight: 600 }}>Rendered appts cannot be deleted.</p>
                    </div>
                }
            />
        );
    };

    const massCompleteInfo = () => {
        openSecondary(<MassCompleteInfo resetSelected={resetSelected} />);
    };
    const completeAppts = () => {
        const cancelledOrCompleted =
            renderedAppts?.length && cancelledAppts?.length
                ? `, but ${renderedAppts?.length + cancelledAppts?.length} are already completed or cancelled and will be skipped.`
                : renderedAppts?.length
                ? `, but ${renderedAppts?.length} are already completed and will be skipped.`
                : cancelledAppts?.length
                ? `, but ${cancelledAppts?.length} are already cancelled and will be skipped.`
                : '.';

        open(
            <CustomDelete
                disableAction={!pendingAppts?.length}
                text={`You’ve selected ${selectedList?.length} appointments. can be marked as completed${cancelledOrCompleted}`}
                info={`Complete Selected Appointments`}
                innerText={`Complete ${pendingAppts?.length} Appts`}
                handleDel={() =>
                    dispatch(
                        appointmentActions.completeMassAppts(
                            {
                                apptIds: selectAllList ? [] : pendingAppts?.map((i) => i?.id),
                                all: selectAllList,
                            },
                            renderParams
                        )
                    )
                }
                actionType={COMPLETE_MASS_APPTS}
                afterSuccess={() => {
                    massCompleteInfo();
                }}
                color={'blue'}
                body={
                    <div className="deleting-client-info-box" style={{ alignItems: 'flex-start' }}>
                        <img src={Images.infoCircle} alt="icon" />
                        <p style={{ fontWeight: 600 }}>Appts already marked as completed cannot be updated</p>
                    </div>
                }
            />
        );
    };

    useEffect(() => {
        if (successConnect?.type === CONNECT_RECONNECT) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(CONNECT_RECONNECT));
            close();
        }
    }, [successConnect]);

    return (
        <div className="schedule-fragment-page">
            {RolePermission([
                PermissionList.APPT_LOCK?.code,
                PermissionList.UNRENDERED_APPT_DELETE.code,
                PermissionList.APPT_RENDER_COMPLETE?.code,
                PermissionList.APPT_RENDER_OWN_SERVICE?.code,
                PermissionList.APPT_COMPLETE_OWN_STAFF?.code,
                PermissionList.APPT_COMPLETE_OWN_DRIVE?.code,
            ]) &&
                !!selectedList?.length && (
                    <SelectActions
                        submitText={'Complete'}
                        submitBtnIcon={Images.completeIcon}
                        submitClaims={completeAppts}
                        submitPermission={RolePermission([
                            PermissionList.APPT_RENDER_COMPLETE?.code,
                            PermissionList.APPT_RENDER_OWN_SERVICE?.code,
                            PermissionList.APPT_COMPLETE_OWN_STAFF?.code,
                            PermissionList.APPT_COMPLETE_OWN_DRIVE?.code,
                        ])}
                        selectInnetText={selectAllList ? 'Clear Selection' : ` Select all ${appointments?.count} across all pages`}
                        actionType={LOCK_UNLOCK}
                        selectedList={selectAllList ? appointments?.count : selectedList?.length}
                        selectAll={() => {
                            if (selectAllList) {
                                resetSelected();
                            } else {
                                setSelectAllList(true);
                                setSelectedList(appointments?.appts?.map((i) => i?.id));
                                setSelectedFullList(appointments?.appts);
                            }
                        }}
                        removeSelected={resetSelected}
                        renderOtherBtns={
                            <div className="flex-align-center" style={{ height: 30, gap: 8 }}>
                                <SignatureActionsMenu
                                    selectedList={selectedList}
                                    selectedFullList={selectedFullList}
                                    selectAllList={selectAllList}
                                    afterSuccess={resetSelected}
                                />

                                {RolePermission([PermissionList.APPT_LOCK?.code]) && (
                                    <>
                                        <button onClick={() => lockUnlock('lock')} className="selector-action-btn">
                                            <img src={Images.lock} alt="lock" />
                                        </button>
                                        <button onClick={() => lockUnlock('unLock')} className="selector-action-btn">
                                            <img src={Images.unlock} alt="lock" />
                                        </button>
                                    </>
                                )}

                                {RolePermission([PermissionList.UNRENDERED_APPT_DELETE?.code]) && (
                                    <button onClick={() => deleteMassAppt()} className="mass-delete">
                                        <img src={Images.redTrashLine} alt="lock" />
                                    </button>
                                )}
                            </div>
                        }
                    />
                )}

            <div style={{ marginTop: 12 }}>
                <ScheduleInformation />
                <FullTable
                    head={apptHead(handleCheck, appointments?.appts, selectedList, selectAllList, globalServices)}
                    body={apptBody(handleCheck, appointments?.appts, selectedList, selectAllList)}
                    loadingType={ACTION_TYPE}
                    list={appointments?.appts}
                    listCount={appointments?.count}
                    handleClick={(id, item) => openSecondary(<ScheduleDetails currentId={item?.id} />)}
                    notYet={apptNotYet}
                    renderRowStyle={(item) => {
                        if (item?.authBillable === false) {
                            return {
                                background: 'repeating-linear-gradient(135deg, #E4E7EC 0px, #E4E7EC 1px, #FEFCE8 1px, #FEFCE8 14px)',
                            };
                        } else if(item?.status === apptEnums.CANCELLED) {
                            return {
                                background: 'repeating-linear-gradient(135deg, #E4E7EC 0px, #E4E7EC 1px, #F2F4F8 1px, #F2F4F8 14px)',
                            }
                        }
                    }}
                    activeRowId={''}
                    margin={'0'}
                    selectedList={selectedList}
                    selectAllList={selectAllList}
                />
            </div>
        </div>
    );
};
